import { useQueries, useQuery } from "@tanstack/react-query";
import { useHotel } from "@/hooks/useHotel";
import { amenitiesService } from "@/services/amenitiesService";
import { categoryService } from "@/services/categoryService";
import { customPagesService } from "@/services/customPagesService";
import { faqService } from "@/services/faqService";
import { feedbacksService } from "@/services/feedbacks-service";
import { hotelService as hotelCatalogService } from "@/services/hotel-service-service";
import { CategoryEntityType } from "@/types/category";
import { HotelServiceStatus } from "@/types/hotelService";
import { resolveGuestExperienceServicesVisibility } from "@/components/pages/dashboard/hooks/guestExperienceServicesVisibility";
import { useActivityLogs } from "@/components/pages/dashboard/hooks/useActivityLogs";
import { useGetPmsSettings } from "@/components/pages/pms/hooks/pms.hooks";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasCorePlatformAccess } from "@/utils/accessControl";
import { shouldShowPmsIndicator } from "@/utils/pmsVisibility";

export type GuestAppChildData = {
  title: string;
  subtitle: string;
  status: GuestExperienceStepStatus;
};

export type GuestExperienceStepData = {
  title: string;
  subtitle: string;
  status: GuestExperienceStepStatus;
  children?: GuestAppChildData[];
};

export type GuestExperienceStepStatus =
  | "active"
  | "warning"
  | "error"
  | "loading"
  | "off";

interface GuestExperienceFeatureFlags {
  showPms: boolean;
  showConfirmationEmail: boolean;
  showPreCheckin: boolean;
  showArrivalMessage: boolean;
  showServices: boolean;
  showAmenities: boolean;
  showPages: boolean;
  showPreCheckout: boolean;
  hasFeedbacksAccess: boolean;
}

interface BuildGuestExperienceStepsOptions extends GuestExperienceFeatureFlags {
  pmsTitle: string;
  confirmationSubtitle: string;
  preCheckinSubtitle: string;
  arrivalMessageSubtitle: string;
  servicesSubtitle: string;
  amenitiesSubtitle: string;
  faqSubtitle: string;
  pagesSubtitle: string;
  preCheckoutSubtitle: string;
  feedbackSubtitle?: string;
  pmsStatus: GuestExperienceStepStatus;
  confirmationStatus: GuestExperienceStepStatus;
  preCheckinStatus: GuestExperienceStepStatus;
  arrivalMessageStatus: GuestExperienceStepStatus;
  servicesStatus: GuestExperienceStepStatus;
  amenitiesStatus: GuestExperienceStepStatus;
  faqStatus: GuestExperienceStepStatus;
  pagesStatus: GuestExperienceStepStatus;
  preCheckoutStatus: GuestExperienceStepStatus;
  feedbackStatus?: GuestExperienceStepStatus;
}

function buildGuestExperienceSteps({
  pmsTitle,
  confirmationSubtitle,
  preCheckinSubtitle,
  arrivalMessageSubtitle,
  servicesSubtitle,
  amenitiesSubtitle,
  faqSubtitle,
  pagesSubtitle,
  preCheckoutSubtitle,
  feedbackSubtitle = "",
  pmsStatus,
  confirmationStatus,
  preCheckinStatus,
  arrivalMessageStatus,
  servicesStatus,
  amenitiesStatus,
  faqStatus,
  pagesStatus,
  preCheckoutStatus,
  feedbackStatus = "warning",
  showPms,
  showConfirmationEmail,
  showPreCheckin,
  showArrivalMessage,
  showServices,
  showAmenities,
  showPages,
  showPreCheckout,
  hasFeedbacksAccess,
}: BuildGuestExperienceStepsOptions): GuestExperienceStepData[] {
  const guestAppChildren: GuestAppChildData[] = [
    ...(showServices
      ? [
          {
            title: "Services",
            subtitle: servicesSubtitle,
            status: servicesStatus,
          },
        ]
      : []),
    ...(showAmenities
      ? [
          {
            title: "Amenities",
            subtitle: amenitiesSubtitle,
            status: amenitiesStatus,
          },
        ]
      : []),
    { title: "FAQ", subtitle: faqSubtitle, status: faqStatus },
    ...(showPages
      ? [{ title: "Pages", subtitle: pagesSubtitle, status: pagesStatus }]
      : []),
  ];
  const guestAppStatus: GuestExperienceStepStatus = guestAppChildren.every(
    (child) => child.status === "active",
  )
    ? "active"
    : "warning";

  return [
    ...(showPms ? [{ title: pmsTitle, subtitle: "", status: pmsStatus }] : []),
    ...(showConfirmationEmail
      ? [
          {
            title: "Confirmation E-mail",
            subtitle: confirmationSubtitle,
            status: confirmationStatus,
          },
        ]
      : []),
    ...(showPreCheckin
      ? [
          {
            title: "Pre check-in",
            subtitle: preCheckinSubtitle,
            status: preCheckinStatus,
          },
        ]
      : []),
    ...(showArrivalMessage
      ? [
          {
            title: "Arrival Message",
            subtitle: arrivalMessageSubtitle,
            status: arrivalMessageStatus,
          },
        ]
      : []),
    ...(guestAppChildren.length > 0
      ? [
          {
            title: "Guest App",
            subtitle: "",
            status: guestAppStatus,
            children: guestAppChildren,
          },
        ]
      : []),
    ...(showPreCheckout
      ? [
          {
            title: "Pre check-out",
            subtitle: preCheckoutSubtitle,
            status: preCheckoutStatus,
          },
        ]
      : []),
    ...(hasFeedbacksAccess
      ? [
          {
            title: "Feedback follow-up",
            subtitle: feedbackSubtitle,
            status: feedbackStatus,
          },
        ]
      : []),
  ];
}

interface DayIsoBounds {
  start: string;
  end: string;
}

function buildActivityFilters(
  bounds: DayIsoBounds,
  service: string,
  enabled: boolean,
) {
  if (!enabled) {
    return undefined;
  }
  return {
    startDate: bounds.start,
    endDate: bounds.end,
    sort: "desc",
    service: [service],
  };
}

function pickTotal<T extends { data?: { total?: number } | undefined }>(
  query: T,
  enabled: boolean,
): number {
  if (!enabled) {
    return 0;
  }
  return query.data?.total ?? 0;
}

function localDayIsoBounds(): DayIsoBounds {
  const now = new Date();
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  );
  return { start: start.toISOString(), end: end.toISOString() };
}

function resolveGuestExperienceSteps(
  params: {
    enabled: boolean;
    hotelId?: string;
    isLoading: boolean;
    pmsTitle: string;
    confirmationEmailsTodayTotal: number;
    preCheckinRequestFinishedTodayTotal: number;
    guestMessagesTodayTotal: number;
    preCheckoutRequestFinishedTodayTotal: number;
    servicesTotal: number;
    hasInternalServices: boolean;
    amenitiesTotal: number;
    faqTotal: number;
    pagesEnabledForHotel: number;
    checkoutFbTotal: number;
    requestFbTotal: number;
    commFbTotal: number;
    isPmsEnabled: boolean;
  } & GuestExperienceFeatureFlags,
): GuestExperienceStepData[] {
  const featureFlags: GuestExperienceFeatureFlags = {
    showPms: params.showPms,
    showConfirmationEmail: params.showConfirmationEmail,
    showPreCheckin: params.showPreCheckin,
    showArrivalMessage: params.showArrivalMessage,
    showServices: params.showServices,
    showAmenities: params.showAmenities,
    showPages: params.showPages,
    showPreCheckout: params.showPreCheckout,
    hasFeedbacksAccess: params.hasFeedbacksAccess,
  };

  if (!params.enabled || !params.hotelId) {
    return buildGuestExperienceSteps({
      ...featureFlags,
      pmsTitle: params.pmsTitle,
      confirmationSubtitle: "",
      preCheckinSubtitle: "",
      arrivalMessageSubtitle: "",
      servicesSubtitle: "",
      amenitiesSubtitle: "",
      faqSubtitle: "",
      pagesSubtitle: "",
      preCheckoutSubtitle: "",
      pmsStatus: "off",
      confirmationStatus: "off",
      preCheckinStatus: "off",
      arrivalMessageStatus: "off",
      servicesStatus: "off",
      amenitiesStatus: "off",
      faqStatus: "off",
      pagesStatus: "off",
      preCheckoutStatus: "off",
      feedbackStatus: "off",
    });
  }

  if (params.isLoading) {
    return buildGuestExperienceSteps({
      ...featureFlags,
      pmsTitle: params.pmsTitle,
      confirmationSubtitle: "…",
      preCheckinSubtitle: "…",
      arrivalMessageSubtitle: "…",
      servicesSubtitle: "…",
      amenitiesSubtitle: "…",
      faqSubtitle: "…",
      pagesSubtitle: "…",
      preCheckoutSubtitle: "…",
      feedbackSubtitle: "…",
      pmsStatus: "loading",
      confirmationStatus: "loading",
      preCheckinStatus: "loading",
      arrivalMessageStatus: "loading",
      servicesStatus: "loading",
      amenitiesStatus: "loading",
      faqStatus: "loading",
      pagesStatus: "loading",
      preCheckoutStatus: "loading",
      feedbackStatus: "loading",
    });
  }

  const feedbackTodayTotal =
    params.checkoutFbTotal + params.requestFbTotal + params.commFbTotal;

  return buildGuestExperienceSteps({
    ...featureFlags,
    pmsTitle: params.pmsTitle,
    confirmationSubtitle: `${params.confirmationEmailsTodayTotal} emails today`,
    preCheckinSubtitle: `${params.preCheckinRequestFinishedTodayTotal} completed today`,
    arrivalMessageSubtitle: `${params.guestMessagesTodayTotal} messages today`,
    // Partners-only (Rapid): no internal services, so no "0 active" counter.
    servicesSubtitle: params.hasInternalServices
      ? `${params.servicesTotal} active`
      : "Guesteria Partners",
    amenitiesSubtitle: `${params.amenitiesTotal} active`,
    faqSubtitle: `${params.faqTotal} active`,
    pagesSubtitle: `${params.pagesEnabledForHotel} enabled`,
    preCheckoutSubtitle: `${params.preCheckoutRequestFinishedTodayTotal} completed today`,
    feedbackSubtitle: `${feedbackTodayTotal} received today`,
    pmsStatus: params.isPmsEnabled ? "active" : "error",
    confirmationStatus:
      params.confirmationEmailsTodayTotal > 0 ? "active" : "warning",
    preCheckinStatus:
      params.preCheckinRequestFinishedTodayTotal > 0 ? "active" : "warning",
    arrivalMessageStatus:
      params.guestMessagesTodayTotal > 0 ? "active" : "warning",
    servicesStatus:
      !params.hasInternalServices || params.servicesTotal > 0
        ? "active"
        : "warning",
    amenitiesStatus: params.amenitiesTotal > 0 ? "active" : "warning",
    faqStatus: params.faqTotal > 0 ? "active" : "warning",
    pagesStatus: params.pagesEnabledForHotel > 0 ? "active" : "warning",
    preCheckoutStatus:
      params.preCheckoutRequestFinishedTodayTotal > 0 ? "active" : "warning",
    feedbackStatus: feedbackTodayTotal > 0 ? "active" : "warning",
  });
}

export const useGuestExperienceSidebarData = () => {
  const { hotelId, hotel } = useHotel();
  const { userProfile } = useAuthenticatedUser();
  const { capabilities, hasFeedbacksAccess } = useAccessCapabilities();
  const enabled = Boolean(hotelId);
  const dayBounds = localDayIsoBounds();

  const showPms = hasCorePlatformAccess(userProfile, "pms");
  const showConfirmationEmail = hasCorePlatformAccess(
    userProfile,
    "notifications",
  );
  const showPreCheckin = capabilities.canUseCheckinCheckout;
  const showPreCheckout = capabilities.canUseCheckinCheckout;
  const showArrivalMessage = capabilities.canUseGuestCommunication;
  const { showServices, hasInternalServices } =
    resolveGuestExperienceServicesVisibility(capabilities);
  const showAmenities = hasCorePlatformAccess(userProfile, "amenities");
  const showPages = hasCorePlatformAccess(userProfile, "custom_pages");
  const pmsSettingsQuery = useGetPmsSettings(showPms ? hotelId : null);

  const {
    total: confirmationEmailsTodayTotal,
    isLoading: confirmationEmailsLoading,
  } = useActivityLogs(
    buildActivityFilters(
      dayBounds,
      "ConfirmationEmailSent",
      showConfirmationEmail,
    ),
  );

  const { total: guestMessagesTodayTotal, isLoading: guestMessagesLoading } =
    useActivityLogs(
      buildActivityFilters(dayBounds, "GuestMessage", showArrivalMessage),
    );

  const {
    total: preCheckinRequestFinishedTodayTotal,
    isLoading: preCheckinActivityLoading,
  } = useActivityLogs(
    buildActivityFilters(dayBounds, "BookingCheckinService", showPreCheckin),
  );

  const {
    total: preCheckoutRequestFinishedTodayTotal,
    isLoading: preCheckoutActivityLoading,
  } = useActivityLogs(
    buildActivityFilters(dayBounds, "BookingCheckoutService", showPreCheckout),
  );

  const categoriesQuery = useQuery({
    queryKey: [
      "dashboard",
      "guestExperience",
      "faqCategories",
      hotelId,
    ] as const,
    queryFn: () =>
      categoryService.findAll({
        entityType: CategoryEntityType.FAQ,
        hotelId: hotelId ?? "",
        page: 1,
        limit: 50,
      }),
    enabled,
    staleTime: 120_000,
  });

  const categoryIds = categoriesQuery.data?.items.map((c) => c.id) ?? [];

  const coreQueries = useQueries({
    queries: [
      {
        queryKey: [
          "dashboard",
          "guestExperience",
          "activeServicesTotal",
          hotelId,
        ] as const,
        queryFn: () =>
          hotelCatalogService.getServices(hotelId ?? "", 1, 1, {
            status: HotelServiceStatus.ACTIVE,
          }),
        enabled: enabled && hasInternalServices,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "guestExperience",
          "activeAmenities",
          hotelId,
        ] as const,
        queryFn: () =>
          amenitiesService.getAll({
            hotelId,
            isActive: true,
            page: 1,
            limit: 500,
          }),
        enabled: enabled && showAmenities,
        staleTime: 120_000,
      },
      {
        queryKey: ["dashboard", "guestExperience", "customPages"] as const,
        queryFn: () => customPagesService.getCustomPages(),
        enabled: enabled && showPages,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "guestExperience",
          "checkoutFeedbackToday",
          hotelId,
          dayBounds.start,
        ] as const,
        queryFn: () =>
          feedbacksService.getAllCheckoutFeedbacks({
            hotelId: hotelId ?? "",
            page: 1,
            limit: 1,
            feedbackStartDateRange: dayBounds.start,
            feedbackEndDateRange: dayBounds.end,
          }),
        enabled: enabled && hasFeedbacksAccess,
        staleTime: 60_000,
      },
      {
        queryKey: [
          "dashboard",
          "guestExperience",
          "requestFeedbackToday",
          hotelId,
          dayBounds.start,
        ] as const,
        queryFn: () =>
          feedbacksService.getAllRequestFeedbacks({
            hotelId: hotelId ?? "",
            page: 1,
            limit: 1,
            feedbackStartDateRange: dayBounds.start,
            feedbackEndDateRange: dayBounds.end,
          }),
        enabled:
          enabled && hasFeedbacksAccess && capabilities.canUseRequestsSystem,
        staleTime: 60_000,
      },
      {
        queryKey: [
          "dashboard",
          "guestExperience",
          "communicationFeedbackToday",
          hotelId,
          dayBounds.start,
        ] as const,
        queryFn: () =>
          feedbacksService.getAllCommunicationFeedbacks({
            hotelId: hotelId ?? "",
            page: 1,
            limit: 1,
            feedbackStartDateRange: dayBounds.start,
            feedbackEndDateRange: dayBounds.end,
          }),
        enabled: enabled && hasFeedbacksAccess,
        staleTime: 60_000,
      },
    ],
  });

  const faqQueries = useQueries({
    queries: categoryIds.map((categoryId) => ({
      queryKey: [
        "dashboard",
        "guestExperience",
        "faqCount",
        hotelId,
        categoryId,
      ] as const,
      queryFn: () =>
        faqService.getAll({
          hotelId: hotelId ?? "",
          categoryId,
          page: 1,
          limit: 500,
        }),
      enabled,
      staleTime: 120_000,
    })),
  });

  const coreLoading =
    enabled &&
    (coreQueries.some((q) => q.isLoading) || categoriesQuery.isLoading);
  const faqLoading =
    enabled && categoryIds.length > 0 && faqQueries.some((q) => q.isLoading);
  const isLoading =
    coreLoading ||
    faqLoading ||
    guestMessagesLoading ||
    confirmationEmailsLoading ||
    preCheckinActivityLoading ||
    preCheckoutActivityLoading ||
    pmsSettingsQuery.isLoading;

  const isPmsEnabled = pmsSettingsQuery.data?.enabled === true;
  const showPmsIndicator = shouldShowPmsIndicator(
    showPms,
    pmsSettingsQuery.data,
  );
  const pmsTitle =
    pmsSettingsQuery.data?.provider ?? hotel?.pms ?? "PMS not connected";
  const servicesTotal = pickTotal(coreQueries[0], hasInternalServices);
  const amenitiesTotal = showAmenities
    ? (coreQueries[1].data?.items?.length ?? 0)
    : 0;
  const pagesEnabledForHotel = showPages
    ? (coreQueries[2].data ?? []).filter(
        (page) => page.hotelId === hotelId && page.enabled,
      ).length
    : 0;
  const checkoutFbTotal = pickTotal(coreQueries[3], hasFeedbacksAccess);
  const requestFbTotal = pickTotal(
    coreQueries[4],
    hasFeedbacksAccess && capabilities.canUseRequestsSystem,
  );
  const commFbTotal = pickTotal(coreQueries[5], hasFeedbacksAccess);
  const faqTotal = faqQueries.reduce(
    (sum, q) => sum + (q.data?.items.length ?? 0),
    0,
  );

  const steps = resolveGuestExperienceSteps({
    enabled,
    hotelId: hotelId ?? undefined,
    isLoading,
    pmsTitle,
    confirmationEmailsTodayTotal,
    preCheckinRequestFinishedTodayTotal,
    guestMessagesTodayTotal,
    preCheckoutRequestFinishedTodayTotal,
    showPms: showPmsIndicator,
    showConfirmationEmail,
    showPreCheckin,
    showArrivalMessage,
    showServices,
    showAmenities,
    showPages,
    showPreCheckout,
    hasFeedbacksAccess,
    servicesTotal,
    hasInternalServices,
    amenitiesTotal,
    faqTotal,
    pagesEnabledForHotel,
    checkoutFbTotal,
    requestFbTotal,
    commFbTotal,
    isPmsEnabled,
  });

  return { steps, isLoading };
};
